import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, LogIn, LogOut, CheckCircle2, Loader2, WifiOff, History } from 'lucide-react';
import clsx from 'clsx';
import { Leader, type LeaderAgentRow, type ActivityLogEntry } from '@/lib/api';
import { REASON_TO_PROFILE, STATUS_DISPLAY } from '@/lib/useLeaderAgents';
import { BREAK_REASONS } from '@/components/softphone/BreakReasonPopover';

/**
 * Lider → agent uzaktan aksiyon modal'ı. Eski Dashboard.js'in "Agent Aksiyonu"
 * swal modal'ının modern karşılığı.
 *
 * Lider TÜM mola sebeplerini atayabilir (agent self-service'te kısıtlı olan
 * eğitim/toplantı/sınav/soru/geri-bildirim dahil — operasyonel kontrol burada).
 */
export function AgentActionsModal({ agent, onClose }: { agent: LeaderAgentRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'actions' | 'history'>('actions');
  const [pending, setPending] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['leader-agents'] });
  // Başarıda: kısa onay göster, cache tazele, sonra kapat (offline agent'ta
  // tablo değişmez ama lider aksiyonun gittiğini görür).
  const onDone = (msg: string) => {
    invalidate();
    setOk(msg);
    setTimeout(onClose, 850);
  };

  const profileMut = useMutation({
    mutationFn: ({ profile, msg }: { profile: any; msg: string }) =>
      Leader.setAgentProfile(agent.extension, profile, msg || undefined),
    onSuccess: () => onDone('Durum güncellendi'),
    onError: (e: any) => setErr(e?.response?.data?.message ?? e?.message ?? 'İşlem başarısız'),
    onSettled: () => setPending(null),
  });

  const queueMut = useMutation({
    mutationFn: (loggedIn: boolean) => Leader.setAgentQueue(agent.extension, loggedIn),
    onSuccess: (_d, loggedIn) => onDone(loggedIn ? 'Kuyruğa alındı' : 'Kuyruktan çıkarıldı'),
    onError: (e: any) => setErr(e?.response?.data?.message ?? e?.message ?? 'İşlem başarısız'),
    onSettled: () => setPending(null),
  });

  const setReady = () => { setErr(null); setPending('ready'); profileMut.mutate({ profile: 'Available', msg: '' }); };
  const assignBreak = (key: string) => {
    const map = REASON_TO_PROFILE[key as keyof typeof REASON_TO_PROFILE];
    if (!map) return;
    setErr(null); setPending(key);
    profileMut.mutate({ profile: map.profile, msg: map.msg });
  };
  const toggleQueue = (loggedIn: boolean) => { setErr(null); setPending(loggedIn ? 'q-in' : 'q-out'); queueMut.mutate(loggedIn); };

  const sd = STATUS_DISPLAY[agent.status];
  const busy = pending !== null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onMouseDown={onClose}>
      <div
        className="w-full max-w-[440px] bg-surface rounded-2xl shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 text-[#08140d] flex items-center justify-center text-[13px] font-semibold">
            {agent.displayName.slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-slate-900 truncate">{agent.displayName}</div>
            <div className="text-[11.5px] text-slate-500 flex items-center gap-1.5">
              <span className="font-mono">{agent.extension}</span>
              <span className={clsx('inline-flex items-center gap-1 px-1.5 py-px rounded-full ring-1 text-[10px]', sd.chip)}>
                <span className={clsx('w-1.5 h-1.5 rounded-full', sd.dot)} />
                {sd.label}{agent.status === 'ozel' && agent.profileMessage ? ` · ${agent.profileMessage}` : ''}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </header>

        {/* Sekmeler — Aksiyonlar / Geçmiş */}
        <div className="flex items-center gap-1 px-3 pt-2 border-b border-slate-100">
          <button
            onClick={() => setTab('actions')}
            className={clsx('px-3 py-1.5 text-[12px] font-medium rounded-t-lg border-b-2 -mb-px transition',
              tab === 'actions' ? 'border-brand-500 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600')}
          >Aksiyonlar</button>
          <button
            onClick={() => setTab('history')}
            className={clsx('inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-t-lg border-b-2 -mb-px transition',
              tab === 'history' ? 'border-brand-500 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600')}
          ><History size={13} /> Geçmiş</button>
        </div>

        {tab === 'history' && <AgentHistory extension={agent.extension} />}

        {tab === 'actions' && (
        <div className="p-4 space-y-4">
          {err && (
            <div className="text-[12px] text-red-600 bg-red-50 ring-1 ring-red-200 rounded-lg px-3 py-2">{err}</div>
          )}
          {ok && (
            <div className="text-[12px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
              <CheckCircle2 size={14} /> {ok}
            </div>
          )}
          {!agent.online && (
            <div className="text-[11.5px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
              <WifiOff size={14} className="mt-0.5 shrink-0" />
              <span>Bu temsilci <strong>çevrimdışı</strong> (3CX'e bağlı değil). Durum/kuyruk ayarlanır ama temsilci uygulamaya bağlanana kadar çağrı almaz — tabloda yine çevrimdışı görünür.</span>
            </div>
          )}

          {/* Kuyruk */}
          <section>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Kuyruk</div>
            <div className="flex gap-2">
              <button
                disabled={busy || agent.queueLoggedIn}
                onClick={() => toggleQueue(true)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium bg-emerald-500 hover:bg-emerald-600 text-[#08140d] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {pending === 'q-in' ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />} Kuyruğa Al
              </button>
              <button
                disabled={busy || !agent.queueLoggedIn}
                onClick={() => toggleQueue(false)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {pending === 'q-out' ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />} Kuyruktan Çıkar
              </button>
            </div>
          </section>

          {/* Durum */}
          <section>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Durum</div>
            <button
              disabled={busy || agent.status === 'hazir'}
              onClick={setReady}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 mb-2 rounded-lg text-[12.5px] font-medium bg-emerald-50 hover:bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {pending === 'ready' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Hazır Yap
            </button>

            <div className="text-[11px] font-medium text-slate-500 mb-1.5">Mola / Özel Durum Ata</div>
            <div className="grid grid-cols-2 gap-1.5">
              {BREAK_REASONS.map((r) => (
                <button
                  key={r.key}
                  disabled={busy}
                  onClick={() => assignBreak(r.key)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[12px] font-medium bg-slate-50 hover:bg-slate-100 text-slate-700 ring-1 ring-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition text-left"
                >
                  {pending === r.key ? <Loader2 size={13} className="animate-spin shrink-0" /> : <r.Icon size={13} className="shrink-0" />}
                  <span className="truncate">{r.label}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
        )}
      </div>
    </div>
  );
}

/* ─── Agent geçmişi (timeline) — backend Leader.agentHistory hazırdı, UI eklendi ── */

function AgentHistory({ extension }: { extension: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-history', extension],
    queryFn: () => Leader.agentHistory(extension, 100),
    refetchInterval: 20_000,
    staleTime: 10_000,
  });
  return (
    <div className="p-4 max-h-[60vh] overflow-y-auto">
      {isLoading && <div className="py-10 text-center text-[12px] text-slate-400">Yükleniyor…</div>}
      {isError && <div className="py-10 text-center text-[12px] text-red-500">Geçmiş alınamadı</div>}
      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <div className="py-10 text-center text-[12px] text-slate-400">Bugün aktivite yok</div>
      )}
      {!isLoading && data && data.length > 0 && (
        <ol className="relative border-l border-slate-200 ml-2">
          {data.map((e) => {
            const ev = mapHistoryEvent(e);
            return (
              <li key={e.id} className="ml-3 pl-3 py-1 relative">
                <span className={clsx('absolute -left-[7px] top-2.5 w-2 h-2 rounded-full ring-2 ring-base', ev.dot)} />
                <div className="flex items-center gap-2 text-[12px] min-w-0">
                  <span className="text-[10.5px] text-slate-400 font-mono tabular-nums w-10 shrink-0">{fmtHistoryTime(e.createdAt)}</span>
                  <span className="font-medium text-slate-800 whitespace-nowrap">{ev.label}</span>
                  {ev.detail && <span className="text-slate-500 truncate font-mono text-[11px]">{ev.detail}</span>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function fmtHistoryTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const HIST_BREAK: Record<string, string> = {
  mola: 'Kısa Mola', yemek: 'Yemek', 'dis-arama': 'Dış Arama', egitim: 'Eğitim',
  toplanti: 'Toplantı', sinav: 'Sınav', soru: 'Soru Molası', 'geri-bildirim': 'Geri Bildirim',
};
const HIST_STATUS: Record<string, string> = {
  hazir: 'Hazır', mola: 'Molada', yemek: 'Yemekte', 'dis-arama': 'Dış Arama',
  ozel: 'Özel durum', 'kuyruk-disi': 'Kuyruk dışı', cevrimdisi: 'Çevrimdışı',
};

function mapHistoryEvent(e: ActivityLogEntry): { label: string; detail?: string; dot: string } {
  const m = (e.meta ?? {}) as Record<string, any>;
  switch (e.type) {
    case 'queue_login': return { label: 'Kuyruğa girdi', detail: 'çağrı almaya hazır', dot: 'bg-emerald-500' };
    case 'queue_logout': return { label: 'Kuyruktan çıktı', dot: 'bg-slate-400' };
    case 'queue_paused': return { label: 'Molaya girdi', detail: HIST_BREAK[m.reason] ?? m.reason ?? 'mola', dot: 'bg-amber-500' };
    case 'queue_resumed': return { label: 'Moladan döndü', dot: 'bg-emerald-500' };
    case 'janus_registered':
    case 'agent_online': return { label: 'Bağlandı', dot: 'bg-slate-400' };
    case 'agent_offline': return { label: 'Çevrimdışı', dot: 'bg-slate-300' };
    case 'incoming_call': return { label: 'Gelen çağrı', detail: m.peer, dot: 'bg-emerald-500' };
    case 'call_answered': return { label: 'Çağrı cevaplandı', detail: m.peer, dot: 'bg-emerald-500' };
    case 'call_started': return { label: 'Giden çağrı', detail: m.target, dot: 'bg-amber-500' };
    case 'call_ended': {
      const sec = typeof m.duration_ms === 'number' ? Math.round(m.duration_ms / 1000) : 0;
      const dir = m.direction === 'outbound' ? 'Giden' : 'Gelen';
      const dur = sec > 0 ? ` · ${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}` : '';
      const answered = m.answered === true || sec > 0;
      return { label: `${dir} çağrı bitti`, detail: `${m.peer ?? ''}${dur}`.trim() || undefined, dot: answered ? 'bg-slate-400' : 'bg-rose-400' };
    }
    case 'call_transferred': return { label: 'Aktarıldı', detail: m.to, dot: 'bg-blue-500' };
    case 'pbx_state_change': return { label: HIST_STATUS[m.status] ?? 'Durum değişti', detail: m.profileMessage, dot: 'bg-slate-400' };
    default: return { label: e.type, dot: 'bg-slate-300' };
  }
}
